import { readStoredLocale } from "@mivo/i18n";
import { ApiMessageCodes } from "@/domains/shared/services/apiMessageCodes";
import type { AxiosError, AxiosInstance } from "axios";

/** Header mirrored by backend ErrorEnvelopeLocale (BR-ENV.1). */
export const PREFERRED_LOCALE_HEADER = "X-Mivo-Preferred-Locale";

type ErrorBody = { code?: unknown; detail?: unknown };

/**
 * Code-first error envelope hardening (BR-FE.1 / api-contract §5):
 * - Prefer preferredLocale header for envelope detail resolution
 * - 409 conflict → surface reload/retry copy; never auto-retry mutations
 */
export function setupErrorEnvelopeInterceptor(api: AxiosInstance): void {
  api.interceptors.request.use((config) => {
    config.headers.set(PREFERRED_LOCALE_HEADER, readStoredLocale());
    return config;
  });

  api.interceptors.response.use(
    (response) => response,
    (error: AxiosError<ErrorBody>) => {
      // Never auto-retry mutating requests on conflict (api-contract §5).
      if (isConflictApiError(error)) {
        const cfg = error.config as (typeof error.config & { __mivoConflictNotRetried?: boolean }) | undefined;
        if (cfg) {
          cfg.__mivoConflictNotRetried = true;
        }
      }
      return Promise.reject(error);
    },
  );
}

export function isConflictApiError(error: unknown): boolean {
  const ax = error as AxiosError<ErrorBody> | undefined;
  if (ax?.response?.status !== 409) {
    return false;
  }
  const code = ax.response.data?.code;
  return code === ApiMessageCodes.conflict || code == null;
}
